import React from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  ActivityIndicator,
} from 'react-native';
import { useSafeAreaInsets, initialWindowMetrics } from 'react-native-safe-area-context';
import { useMarketplaceColors } from '@/constants/residents/marketplaceColors';

interface PaymentModalFooterProps {
  onCancel: () => void;
  onProceed: () => void;
  cancelText?: string;
  proceedText?: string;
  proceedDisabled?: boolean;
  loading?: boolean;
  showCancel?: boolean;
  variant?: 'default' | 'single' | 'autopay';
  style?: any;
}

/**
 * Modal footer with Cancel and Proceed buttons
 * Matches Figma design for payment modals
 */
export const PaymentModalFooter: React.FC<PaymentModalFooterProps> = ({
  onCancel,
  onProceed,
  cancelText = 'Cancel',
  proceedText = 'Proceed',
  proceedDisabled = false,
  loading = false,
  showCancel = true,
  variant = 'default',
  style,
}) => {
  const MARKETPLACE_COLORS = useMarketplaceColors();
  const insets = useSafeAreaInsets();
  const bottomPadding = Math.max(insets.bottom, initialWindowMetrics?.insets?.bottom ?? 0, 24);

  const handleProceed = () => {
    if (!proceedDisabled && !loading) {
      onProceed();
    }
  };

  const handleCancel = () => {
    onCancel();
  };

  if (variant === 'autopay') {
    // Autopay variant with stacked buttons
    return (
      <View style={[styles.container, styles.autopayContainer, { paddingBottom: bottomPadding }, style]}>
        <Text style={[styles.autopayTitle, { color: MARKETPLACE_COLORS.textPrimary }]}>
          Save time by setting up a recurring payment?
        </Text>
        <View style={styles.autopayButtons}>
          <TouchableOpacity
            style={[styles.autopayPrimaryButton, { backgroundColor: MARKETPLACE_COLORS.accentBlueSolid }]}
            onPress={handleProceed}
            activeOpacity={0.8}
            disabled={loading}
          >
            {loading ? (
              <ActivityIndicator size="small" color={MARKETPLACE_COLORS.textPrimary} />
            ) : (
              <Text style={[styles.autopayPrimaryText, { color: MARKETPLACE_COLORS.textPrimary }]}>{proceedText}</Text>
            )}
          </TouchableOpacity>
          <TouchableOpacity
            style={styles.autopaySecondaryButton}
            onPress={handleCancel}
            activeOpacity={0.8}
          >
            <Text style={[styles.autopaySecondaryText, { color: MARKETPLACE_COLORS.accentBlue }]}>{cancelText}</Text>
          </TouchableOpacity>
        </View>
      </View>
    );
  }

  if (variant === 'single') {
    // Single button variant
    return (
      <View style={[styles.container, { paddingBottom: bottomPadding }, style]}>
        <TouchableOpacity
          style={[
            styles.singleButton,
            { backgroundColor: MARKETPLACE_COLORS.accentBlueSolid },
            proceedDisabled && { backgroundColor: MARKETPLACE_COLORS.buttonDisabled },
          ]}
          onPress={handleProceed}
          activeOpacity={0.8}
          disabled={proceedDisabled || loading}
        >
          {loading ? (
            <ActivityIndicator size="small" color={MARKETPLACE_COLORS.textPrimary} />
          ) : (
            <Text style={[
              styles.proceedText,
              { color: MARKETPLACE_COLORS.textPrimary },
              proceedDisabled && { color: MARKETPLACE_COLORS.buttonDisabledText },
            ]}>{proceedText}</Text>
          )}
        </TouchableOpacity>
      </View>
    );
  }

  // Default two-button variant
  return (
    <View style={[styles.container, { paddingBottom: bottomPadding }, style]}>
      <View style={styles.buttonsRow}>
        {showCancel && (
          <TouchableOpacity
            style={[styles.cancelButton, { borderColor: MARKETPLACE_COLORS.textPrimary }]}
            onPress={handleCancel}
            activeOpacity={0.8}
          >
            <Text style={[styles.cancelText, { color: MARKETPLACE_COLORS.textPrimary }]}>{cancelText}</Text>
          </TouchableOpacity>
        )}
        <TouchableOpacity
          style={[
            styles.proceedButton,
            { backgroundColor: MARKETPLACE_COLORS.accentBlueSolid },
            !showCancel && styles.fullWidthButton,
            proceedDisabled && { backgroundColor: MARKETPLACE_COLORS.buttonDisabled },
          ]}
          onPress={handleProceed}
          activeOpacity={0.8}
          disabled={proceedDisabled || loading}
        >
          {loading ? (
            <ActivityIndicator size="small" color={MARKETPLACE_COLORS.textPrimary} />
          ) : (
            <Text style={[
              styles.proceedText,
              { color: MARKETPLACE_COLORS.textPrimary },
              proceedDisabled && { color: MARKETPLACE_COLORS.buttonDisabledText },
            ]}>{proceedText}</Text>
          )}
        </TouchableOpacity>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    // Padding - bottom is handled dynamically for safe area insets
    paddingHorizontal: 24,
    paddingTop: 24,
  },
  buttonsRow: {
    flexDirection: 'row',
    gap: 20,
  },
  cancelButton: {
    flex: 1,
    height: 40,
    borderWidth: 1,
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
  },
  cancelText: {
    fontSize: 14,
    fontWeight: '500',
    lineHeight: 21,
  },
  proceedButton: {
    flex: 1,
    height: 40,
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
  },
  singleButton: {
    height: 40,
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
  },
  fullWidthButton: {
    flex: 1,
  },
  proceedText: {
    fontSize: 14,
    fontWeight: '500',
    lineHeight: 21,
  },
  // Autopay variant styles
  autopayContainer: {
    gap: 16,
  },
  autopayTitle: {
    fontSize: 16,
    fontWeight: '400',
    lineHeight: 24,
  },
  autopayButtons: {
    gap: 6,
  },
  autopayPrimaryButton: {
    height: 40,
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
  },
  autopayPrimaryText: {
    fontSize: 14,
    fontWeight: '500',
    lineHeight: 21,
  },
  autopaySecondaryButton: {
    height: 40,
    alignItems: 'center',
    justifyContent: 'center',
  },
  autopaySecondaryText: {
    fontSize: 14,
    fontWeight: '500',
    lineHeight: 21,
  },
});
